import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const CartContext = createContext(null);
export const FREE_SHIPPING = 50.0;
export const SHIPPING_COST = 6.9;

function load(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("lm_cart", []));
  const [wishlist, setWishlist] = useState(() => load("lm_wishlist", []));
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { localStorage.setItem("lm_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem("lm_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const addItem = useCallback((product, size, qty = 1) => {
    setItems((prev) => {
      const key = `${product.id}|${size.label}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { key, product_id: product.id, name_it: product.name_it, name_en: product.name_en, image: product.image, size: size.label, price: size.price, qty }];
    });
  }, []);

  const setQty = useCallback((key, qty) => {
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))));
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setItems([]), []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((prev) => (prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]));
  }, []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const shipping = subtotal >= FREE_SHIPPING || subtotal === 0 ? 0 : SHIPPING_COST;

  return (
    <CartContext.Provider value={{ items, addItem, setQty, removeItem, clearCart, subtotal, count, shipping, wishlist, toggleWishlist, drawerOpen, setDrawerOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
